import React, { useEffect, useState } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';

import {
	STOCK_REDUCTION_REASON_OPTIONS,
} from '../../../common/utils/serviceDocket/stockReductionReason';
import { Dialog, Text } from '../../components';
import { PrimaryButton, SecondaryButton } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const getDefaultReasons = requests => (requests || []).reduce((reasons, request) => ({
	...reasons,
	[request.itemId]: request.defaultReason,
}), {});

const StockReductionReasonDialog = ({
	requests,
	onSave,
	onSkip,
}) => {
	const styles = useThemedStyles(createStyles);
	const [selectedReasons, setSelectedReasons] = useState({});

	useEffect(() => {
		setSelectedReasons(getDefaultReasons(requests));
	}, [requests]);

	return (
		<Dialog
			isOpen={Boolean(requests?.length)}
			onClose={onSkip}
			closeStyle={{ top: 4, right: 4 }}
		>
			<View style={styles.container}>
				<Text style={styles.title}>What happened to this stock?</Text>
				<Text style={styles.intro}>
					Choose one reason for each line, or skip to save the confirmation without adding reasons.
				</Text>
				<ScrollView
					style={styles.list}
					scrollIndicatorInsets={{ right: 1 }}
				>
					{(requests || []).map(request => (
						<View key={request.itemId} style={styles.item}>
							<Text style={styles.product}>
								{request.productName}{request.selection ? ` · Selection ${request.selection}` : ''}
							</Text>
							<Text style={styles.prompt}>
								You&apos;ve reduced this by {request.unexplainedQuantity}. What happened?
							</Text>
							<Text style={styles.summary}>
								Expected {request.expectedQuantity} · Confirmed {request.confirmedQuantity}
							</Text>
							<View style={styles.options}>
								{STOCK_REDUCTION_REASON_OPTIONS.map((option, optionIndex) => {
									const selected = selectedReasons[request.itemId] === option.field;
									return (
										<TouchableOpacity
											key={option.field}
											accessibilityRole="button"
											accessibilityState={{ selected }}
											onPress={() => setSelectedReasons(reasons => ({
												...reasons,
												[request.itemId]: option.field,
											}))}
											style={[
												styles.option,
												optionIndex % 2 === 0 && styles.optionLeft,
												selected && styles.optionSelected,
											]}
										>
											<Text style={[styles.optionText, selected && styles.optionTextSelected]}>
												{option.label}
											</Text>
										</TouchableOpacity>
									);
								})}
							</View>
						</View>
					))}
				</ScrollView>
				<View style={styles.actions}>
					<PrimaryButton
						size="compact"
						label="Save reasons and confirm"
						onPress={() => onSave(selectedReasons)}
					/>
					<SecondaryButton
						label="Skip and confirm"
						onPress={onSkip}
					/>
				</View>
			</View>
		</Dialog>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	container: {
		maxWidth: 560,
	},
	title: {
		...type.cardTitle,
		color: colors.text,
		paddingRight: 36,
	},
	intro: {
		...type.secondary,
		color: colors.textSecondary,
		marginTop: 6,
	},
	list: {
		maxHeight: 340,
		marginTop: space.md,
	},
	item: {
		backgroundColor: colors.raised,
		borderColor: colors.raisedBorder,
		borderRadius: radius.card,
		borderWidth: 1,
		marginBottom: 10,
		padding: space.md,
	},
	product: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	prompt: {
		...type.bodySmall,
		color: colors.text,
		marginTop: space.xs,
	},
	summary: {
		...type.caption,
		color: colors.textSecondary,
		marginTop: 2,
	},
	options: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginTop: space.sm,
	},
	option: {
		alignItems: 'center',
		backgroundColor: colors.card,
		borderColor: colors.borderStrong,
		borderRadius: radius.control,
		borderWidth: 1,
		justifyContent: 'center',
		marginBottom: space.sm,
		minHeight: 50,
		paddingHorizontal: 7,
		width: '48%',
	},
	optionLeft: {
		marginRight: '4%',
	},
	optionSelected: {
		backgroundColor: colors.primary,
		borderColor: colors.primary,
	},
	optionText: {
		...type.label,
		color: colors.text,
		textAlign: 'center',
	},
	optionTextSelected: {
		color: colors.onPrimary,
	},
	actions: {
		gap: space.sm,
	},
});

export default StockReductionReasonDialog;
